// Единственный WebSocket-клиент приложения.
// При успешной авторизации открывается, при logout — закрывается.
// Auth через httpOnly cookie (браузер отправляет автоматически).

import type { WsPwaInbound, WsPwaOutbound } from '@claude-remote/shared';

type Listener = (msg: WsPwaInbound) => void;

export class PwaWebsocket {
  private socket: WebSocket | null = null;
  private connected = false;
  private closed = false;
  private backoff = 500;
  private reconnectTimer: number | null = null;
  private readonly listeners = new Set<Listener>();
  private readonly subscriptions = new Set<string>();

  /** Открывает соединение. Cookie отправляется автоматически. */
  connect(): void {
    if (this.connected && this.socket?.readyState === WebSocket.OPEN) return;
    this.connected = true;
    this.closed = false;
    this.open();
  }

  close(): void {
    this.closed = true;
    this.connected = false;
    this.subscriptions.clear();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.socket = null;
  }

  onMessage(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  subscribe(sessionIds: string[]): void {
    for (const id of sessionIds) this.subscriptions.add(id);
    this.send({ type: 'subscribe', sessionIds });
  }

  unsubscribe(sessionIds: string[]): void {
    for (const id of sessionIds) this.subscriptions.delete(id);
    this.send({ type: 'unsubscribe', sessionIds });
  }

  send(msg: WsPwaOutbound): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(msg));
    return true;
  }

  private open(): void {
    if (!this.connected || this.closed) return;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    // Cookies отправляются автоматически для same-origin WebSocket.
    // Query-параметр token больше не нужен.
    const url = `${proto}://${location.host}/ws/pwa`;
    const socket = new WebSocket(url);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.backoff = 500;
      // Переподписываемся на всё после реконнекта.
      if (this.subscriptions.size > 0) {
        this.send({ type: 'subscribe', sessionIds: Array.from(this.subscriptions) });
      }
    });

    socket.addEventListener('message', (ev) => {
      let msg: WsPwaInbound;
      try {
        msg = JSON.parse(ev.data as string) as WsPwaInbound;
      } catch {
        return;
      }
      for (const l of this.listeners) l(msg);
    });

    socket.addEventListener('close', () => {
      this.socket = null;
      if (!this.closed) this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      // close сработает отдельно
    });
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    const delay = this.backoff;
    this.backoff = Math.min(this.backoff * 2, 30_000);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }
}

export const ws = new PwaWebsocket();
